import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDropzone } from "react-dropzone";
import { InsertDriveFile, Delete } from "@mui/icons-material";
import UtilsFncs from "utils/UtilsFncs";

const useStyles = makeStyles({
  dropzone: {
    padding: "2rem 2rem 1rem",
    backgroundColor: "#fff",
    border: "1px dashed #dee2e6",
    borderRadius: ".375rem",
    textAlign: "center",
    color: "#8898aa",
    cursor: "pointer",
  },
  previewCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    border: "1px solid #dee2e6",
    borderRadius: ".375rem",
    backgroundColor: "#fff",
    marginTop: 8,
  },
  previewImage: {
    width: 72,
    height: 72,
    objectFit: "cover",
    borderRadius: 6,
    flexShrink: 0,
    border: "1px solid #e9ecef",
  },
  previewIcon: {
    width: 72,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "#f6f9fc",
    flexShrink: 0,
  },
});

function isImageFile(file) {
  return file?.type?.startsWith("image/");
}

function formatFileName(name, maxLength = 32) {
  if (!name || name.length <= maxLength) return name;
  const dotIndex = name.lastIndexOf(".");
  const ext = dotIndex > 0 ? name.slice(dotIndex) : "";
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const keep = maxLength - ext.length - 3;
  return `${base.slice(0, Math.max(keep, 8))}...${ext}`;
}

function FilePreview({ file, previewUrl, onRemove, disabled, classes }) {
  return (
    <Box className={classes.previewCard}>
      {previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt={file.name}
          className={classes.previewImage}
        />
      ) : (
        <Box className={classes.previewIcon}>
          <InsertDriveFile style={{ fontSize: 36, color: "#8898aa" }} />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          style={{ fontWeight: 600, wordBreak: "break-word" }}
        >
          {formatFileName(file.name)}
        </Typography>
        <Typography variant="caption" style={{ color: "#8898aa" }}>
          {UtilsFncs.GetFileSize(file)}
        </Typography>
        <Typography
          variant="caption"
          style={{ color: "#2dce89", display: "block", marginTop: 4 }}
        >
          Arquivo anexado
        </Typography>
      </Box>
      <Tooltip title="Remover arquivo">
        <IconButton
          onClick={onRemove}
          disabled={disabled}
          size="small"
          aria-label="Remover arquivo"
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function UploadFile({
  multiple = false,
  accept = null,
  message = "Selecione um arquivo ou arraste aqui",
  validator,
  onChangeFiles,
  disabled,
  resetKey,
}) {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const onChangeFilesRef = useRef(onChangeFiles);
  const previewUrlsRef = useRef([]);
  const prevResetKeyRef = useRef(resetKey);

  useEffect(() => {
    onChangeFilesRef.current = onChangeFiles;
  }, [onChangeFiles]);

  const revokePreviews = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
  }, []);

  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;
    revokePreviews();
    setFiles([]);
    setPreviews([]);
  }, [resetKey, revokePreviews]);

  useEffect(() => {
    revokePreviews();
    const urls = files.map((file) =>
      isImageFile(file) ? URL.createObjectURL(file) : null,
    );
    previewUrlsRef.current = urls.filter(Boolean);
    setPreviews(urls);

    return () => revokePreviews();
  }, [files, revokePreviews]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      setFiles((prev) =>
        multiple ? [...prev, ...acceptedFiles] : acceptedFiles,
      );
    },
    [multiple],
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple,
    accept,
    validator,
    onDrop,
    disabled,
  });

  useEffect(() => {
    if (typeof onChangeFilesRef.current === "function") {
      onChangeFilesRef.current(files);
    }
  }, [files]);

  const removeFileFromList = useCallback((position) => {
    setFiles((prev) => {
      const next = [...prev];
      next.splice(position, 1);
      return next;
    });
  }, []);

  const showDropzone = multiple || files.length === 0;

  return (
    <Box>
      {showDropzone && (
        <div {...getRootProps({ className: classes.dropzone })}>
          <input {...getInputProps()} disabled={disabled} />
          <p>{message}</p>
        </div>
      )}
      {files.map((file, index) => (
        <FilePreview
          key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
          file={file}
          previewUrl={previews[index]}
          onRemove={() => removeFileFromList(index)}
          disabled={disabled}
          classes={classes}
        />
      ))}
    </Box>
  );
}
